// @givmo/board — one signature-class act, start to finish (CBG-SEC2 SEC2-WEB; D-CBG-17, D-CBG-18).
//
// The signature-class acts — a consent's signature and its withdrawal, the minutes' signature, a
// record's acknowledgment, the director's own disclosure, the year's answers and (CBG-RC10) a
// signature approving a closed session's minutes — share one shape, so they share this file:
//
//   * THE INTENT SENTENCE comes from `/me` and its id rides the act (contract §8.3). Until `/me`
//     answers the button waits: the words are never invented.
//   * SIGN RUNS THE ACT THROUGH THE GATE (`director/signing-gate.tsx`): an open window signs at once;
//     a shut one brings the sheet up by itself, and the act is sent once more after it.
//   * NEITHER FACTOR (the account has no passkey and no authenticator, both read as strictly false)
//     opens Account Security's setup offer instead of the sheet, and an act set up for runs through
//     the gate once a factor exists, whose refusal brings the one confirmation (derivation §1.5).
//   * TWO REFUSALS IN A ROW are an inline line under the button, never a loop.
//
// The row is the approved `ActRow` (frames `w-*`): the sentence above, the button with one quiet
// line beside it, the error slot beneath.
//
// A SEAT WHOSE ACCOUNT CAN'T SIGN HERE (CBG-SEC2-L4-MOVE; D-CBG-23 flow (b); frames b1, b1n): where
// Board's verdict for this account on this board says another Givmo account is required, the row is
// the move's pointer instead (`move-pointer.tsx`): why, and the two ways on. An act the backend
// refuses for that reason re-reads `/me`, so a verdict that changed while the page was open turns the
// row into the pointer too.
//
// A SEAT WHOSE ACCESS ENDED WHILE THE PAGE WAS OPEN (CBG-FIX34; `director/access-refusal.tsx`): an
// act refused "You no longer have access to this board." re-reads `/me` before it ends, Sign pending
// meanwhile, so a board `/me` no longer lists has moved the page to its no-seat state by the time the
// act says anything, and no Sign is left to press.
//
// THE PAUSE AFTER A SIGN-IN METHOD IS REMOVED (CBG-FIX127; `director/signing-pause.ts`): while `/me`
// says it runs, the line beside Sign says until when (S1), and Sign brings up no sheet, since
// confirming can't open signing: it says the same in the error slot instead, and asks `/me` again.
// An account with neither factor still gets the setup offer from Sign; only the sheet is skipped.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ErrorMessage, testIdProp } from '@givmo/design-system';
import { useOptionalStepUpHost } from '@givmo/step-up';
import { displayClockTime } from '../../lib/display';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { useSearch } from '../../board-routing';
import { directorOrgOf } from '../../director/director-board';
import { useBoardDirectorHost, type BoardDirectorSetupOutcome } from '../../director/director-host';
import {
  boardDirectorMeKey,
  directorBoardFor,
  intentStatementFor,
  useDirectorMe,
} from '../../director/director-me';
import {
  DIRECTOR_REFUSALS,
  isAccessDenied,
  type BoardSigningAct,
  type DirectorIntentStatement,
} from '../../director/director-wire';
import { signingRowFor } from '../../director/signing-copy';
import { pauseEndsAt, signingPauseWords, useSigningPause } from '../../director/signing-pause';
import {
  holdsAuthenticatorOf,
  needsFactorSetup,
  readHoldsAuthenticator,
  useBoardSigningGate,
  useLatestStepUpHost,
} from '../../director/signing-gate';
import { DirectorShellTakeover } from './director-shell';
import { DirectorMovePointer } from './move-pointer';

/** What the act row says after a second refusal: nothing happened, and what the director can do. */
export const REFUSED_TWICE: Record<BoardSigningAct, string> = {
  consent_signature: 'Nothing was signed: signing didn’t open. Try again, or sign out and back in.',
  consent_signature_withdrawal:
    'Nothing changed: signing didn’t open. Try again, or sign out and back in.',
  minutes_signature: 'Nothing was signed: signing didn’t open. Try again, or sign out and back in.',
  // CBG-RC10: the minutes signature's line, approved, reused.
  minutes_approval_signature:
    'Nothing was signed: signing didn’t open. Try again, or sign out and back in.',
  record_acknowledgment:
    'Nothing was recorded: signing didn’t open. Try again, or sign out and back in.',
  conflict_disclosure:
    'Nothing was recorded: signing didn’t open. Try again, or sign out and back in.',
  // The consent and minutes signatures' line (not drawn; approved, CBG-YR2-COPY1).
  coi_questionnaire: 'Nothing was signed: signing didn’t open. Try again, or sign out and back in.',
};

/**
 * Focus back on the control that started an act, once the act ends with the director still on the
 * page. A working button is disabled, and a browser drops focus from a disabled control, so the sheet
 * that opened over the act finds nothing to return focus to when it closes. Only where focus has
 * nowhere else to be, and only to a control still on the page.
 */
function restoreFocus(target: HTMLElement | null) {
  if (target === null) return;
  setTimeout(() => {
    const active = document.activeElement;
    if (!target.isConnected || (active !== null && active !== document.body)) return;
    target.focus();
  }, 0);
}

export interface DirectorActCallbacks<T> {
  /** The act is recorded (the gate's retry included). */
  onDone: (value: T) => void | Promise<void>;
  /** Anything but the gate's own endings: a changed text (409), a changed sentence (409), a failure. */
  onFailed: (error: unknown) => void;
}

export interface DirectorActState {
  act: BoardSigningAct;
  /** The words this act affirms, or null until `/me` says them. */
  intent: DirectorIntentStatement | null;
  pending: boolean;
  /**
   * The act is pending and the director isn't being asked: Board's own read before Sign chooses,
   * the act, the gate's read of the account before its sheet, the token refresh and the retry.
   * Never while the sheet waits on the director. Sign shows a spinner only for this.
   */
  working: boolean;
  /** Refused again after a confirmation. Cleared by the next press. */
  refusedTwice: boolean;
  /**
   * CBG-FIX127: S1, while the pause after a sign-in method's removal runs, or null. The row shows it
   * beside Sign until a press says it in the error slot (`paused`).
   */
  pauseLine: string | null;
  /** Sign was pressed during the pause: no sheet opened, and the row says why. Cleared by the next press. */
  paused: boolean;
  /** Account Security's setup offer is showing, opened from Sign. */
  offering: boolean;
  /** Press the button: `build` makes the act's thunk from the intent statement's id. */
  begin: (build: (intentStatementId: string) => () => Promise<unknown>) => void;
  /** The setup offer ended. */
  settleOffer: (outcome: BoardDirectorSetupOutcome) => void;
  /**
   * The sheet, while a confirmation is outstanding: render it inside the page. `null` where the
   * host's provider draws the sheet.
   */
  dialog: ReactNode;
}

export function useDirectorAct<T>(
  act: BoardSigningAct,
  callbacks: DirectorActCallbacks<T>,
): DirectorActState {
  const gate = useBoardSigningGate(signingRowFor(act));
  const meQuery = useDirectorMe();
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [refusedTwice, setRefusedTwice] = useState(false);
  const [paused, setPaused] = useState(false);
  const [offering, setOffering] = useState(false);
  const pauseEnd = useSigningPause();
  const held = useRef<(() => Promise<unknown>) | null>(null);
  // The control the director pressed, for focus once the act ends.
  const pressed = useRef<HTMLElement | null>(null);
  const latest = useRef(callbacks);
  latest.current = callbacks;

  const intent = intentStatementFor(meQuery.data, act);

  const runThrough = async (
    thunk: () => Promise<unknown>,
    accountHasPasskey: boolean | null | undefined,
  ) => {
    setPending(true);
    const outcome = await gate.run(thunk, accountHasPasskey);
    if (outcome.kind === 'failed' && isAccessDenied(outcome.error)) {
      // CBG-FIX34: `/me` first, still pending, so a board it no longer lists has moved the page.
      await queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
    }
    setPending(false);
    switch (outcome.kind) {
      case 'done':
        // The confirmation, if there was one, re-issued the sign-in: `/me` now reports the window.
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        await latest.current.onDone(outcome.value as T);
        return;
      case 'cancelled':
        restoreFocus(pressed.current);
        return;
      case 'refused-twice':
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        setRefusedTwice(true);
        restoreFocus(pressed.current);
        return;
      case 'needs-setup':
        if (host.setupOffer !== null) {
          held.current = thunk;
          setOffering(true);
        } else {
          setRefusedTwice(true);
        }
        return;
      case 'failed':
        // CBG-SEC2-L4-MOVE: refused for another Givmo account: `/me` says so now, and the row
        // becomes the move's pointer.
        if (
          isGivmoError(outcome.error) &&
          firstErrorCode(outcome.error) === DIRECTOR_REFUSALS.anotherAccountRequired
        ) {
          void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        }
        latest.current.onFailed(outcome.error);
        restoreFocus(pressed.current);
        return;
    }
  };

  /**
   * CBG-FIX127: Sign during the pause brings up no sheet, since confirming can't open signing. The row
   * says why (S1), and `/me` is asked again, so a later removal's end replaces the one shown.
   */
  const holdForThePause = () => {
    setPaused(true);
    void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
    restoreFocus(pressed.current);
  };

  const begin = (build: (intentStatementId: string) => () => Promise<unknown>) => {
    if (intent === null || pending) return;
    const active = document.activeElement;
    pressed.current = active instanceof HTMLElement && active !== document.body ? active : null;
    const thunk = build(intent.id);
    setRefusedTwice(false);
    setPaused(false);
    const signing = meQuery.data?.signing;
    if (host.setupOffer === null || signing?.account_has_passkey !== false) {
      if (pauseEnd !== null) {
        holdForThePause();
        return;
      }
      void runThrough(thunk, signing?.account_has_passkey);
      return;
    }
    // An account `/me` says holds no passkey may need Account Security's setup offer instead of a
    // prompt. The choice waits for the host's answer about the authenticator (a host that reads the
    // account on demand reads it now, AS-WEB15), with Sign pending meanwhile and nothing typed lost.
    void (async () => {
      setPending(true);
      const holdsAuthenticator = await readHoldsAuthenticator(stepUpHost);
      setPending(false);
      if (needsFactorSetup(false, holdsAuthenticator)) {
        held.current = thunk;
        setOffering(true);
        return;
      }
      if (pauseEnd !== null) {
        holdForThePause();
        return;
      }
      await runThrough(thunk, false);
    })();
  };

  const settleOffer = (outcome: BoardDirectorSetupOutcome) => {
    setOffering(false);
    const thunk = held.current;
    held.current = null;
    if (outcome !== 'created' || thunk === null) return;
    // Setting a factor up doesn't open the window: the act goes through the gate, and its refusal
    // brings the one confirmation with the factor just made. The act is pending from here, through
    // the read of `/me` that says which factor now exists: Sign spins, and a press meanwhile starts
    // nothing (review-43 S1). A read that fails ends it in the act's ordinary error.
    void (async () => {
      setPending(true);
      let accountHasPasskey: boolean | null | undefined;
      let pausedNow = false;
      try {
        const fresh = await meQuery.refetch();
        if (fresh.isError) throw fresh.error;
        accountHasPasskey = fresh.data?.signing.account_has_passkey;
        pausedNow = pauseEndsAt(fresh.data?.signing, Date.now()) !== null;
      } catch (error) {
        setPending(false);
        latest.current.onFailed(error);
        restoreFocus(pressed.current);
        return;
      }
      if (pausedNow) {
        // A factor made during the pause doesn't end it: no sheet, the row says until when.
        setPending(false);
        setPaused(true);
        restoreFocus(pressed.current);
        return;
      }
      await runThrough(thunk, accountHasPasskey);
    })();
  };

  return {
    act,
    intent,
    pending,
    working: pending && !gate.prompting,
    refusedTwice,
    pauseLine: pauseEnd === null ? null : signingPauseWords.acts(pauseEnd),
    paused: paused && pauseEnd !== null,
    offering,
    begin,
    settleOffer,
    dialog: gate.dialog,
  };
}

/**
 * What an act's error slot says: the second refusal's line, S1 once Sign was pressed during the pause
 * (CBG-FIX127), else the page's own error for the act.
 */
export function actMessage(state: DirectorActState, error: string | null): string | null {
  if (state.refusedTwice) return REFUSED_TWICE[state.act];
  if (state.paused && state.pauseLine !== null) return state.pauseLine;
  return error;
}

export interface DirectorActRowProps {
  state: DirectorActState;
  label: string;
  tone?: 'primary' | 'secondary';
  onPress: () => void;
  /** The page's own error for this act (a changed text, a transport failure), shown in the slot. */
  error?: string | null;
  /** `director-consent` → `-intent`, `-window-line`, `-act-error`. */
  testIdPrefix: string;
  buttonTestId: string;
}

/** The intent sentence the act records, its button, the one quiet line beside it, and the error slot. */
export function DirectorActRow({
  state,
  label,
  tone = 'primary',
  onPress,
  error = null,
  testIdPrefix,
  buttonTestId,
}: DirectorActRowProps) {
  const meQuery = useDirectorMe();
  const { methodWord } = useBoardDirectorHost();
  const stepUpHost = useOptionalStepUpHost();
  const signing = meQuery.data?.signing;

  // The line names the setup only for an account with neither factor, and a host that reads the
  // account on demand (AS-WEB15) hasn't read the authenticator until asked. Ask once, only where the
  // line could apply (the window shut, `/me` saying no passkey); until the answer is in, no line.
  const askedHost = useRef(false);
  const mayNeedSetup =
    signing !== undefined && signing.open !== true && signing.account_has_passkey === false;
  const prepare = stepUpHost?.prepare;
  useEffect(() => {
    if (!mayNeedSetup || prepare === undefined || askedHost.current) return;
    askedHost.current = true;
    void prepare().catch(() => undefined);
  }, [mayNeedSetup, prepare]);

  // CBG-SEC2-L4-MOVE (frames b1, b1n): this account can't sign for this board.
  const board = directorBoardFor(meQuery.data, directorOrgOf(useSearch()));
  if (board?.another_account_required === true) {
    return (
      <DirectorMovePointer
        orgRef={board.org_ref}
        blocks="sign"
        testId={`${testIdPrefix}-move-pointer`}
      />
    );
  }

  // CBG-FIX127: during the pause, its line (S1) takes this place, until a press says it beneath.
  const line =
    state.pauseLine !== null
      ? state.paused
        ? null
        : state.pauseLine
      : signing?.open === true && signing.closes_at !== null
        ? `No need to confirm again until ${displayClockTime(signing.closes_at)}.`
        : needsFactorSetup(signing?.account_has_passkey, holdsAuthenticatorOf(stepUpHost))
          ? `Before your first signature, you’ll set up ${methodWord} or an authenticator app.`
          : null;
  const shown = actMessage(state, error);

  return (
    <div className="flex flex-col gap-12">
      {state.intent !== null ? (
        <p
          className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp(`${testIdPrefix}-intent`)}
        >
          {state.intent.text}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-16 gap-y-8">
        <Button
          appearance="saas"
          tone={tone}
          // While the act is pending and the director isn't being asked: never behind the sheet
          // (frames `w-lapse-*`), always through the reads and the refresh around it.
          loading={state.working}
          disabled={state.intent === null}
          onClick={onPress}
          testId={buttonTestId}
        >
          {label}
        </Button>
        {line !== null ? (
          <span
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            {...testIdProp(`${testIdPrefix}-window-line`)}
          >
            {line}
          </span>
        ) : null}
      </div>
      {shown !== null ? (
        <ErrorMessage testId={`${testIdPrefix}-act-error`}>{shown}</ErrorMessage>
      ) : null}
    </div>
  );
}

/**
 * Account Security's setup offer as a page in Board's shell: after joining (`m1-joined`, the default
 * variant) and from Sign on an account with neither factor (`w-setup-2-offer`, `from-sign`). From
 * inside a page — the minutes' Sign, the disclosure dialog — it takes over that page's column, and
 * the page stays mounted behind it with the act it holds.
 */
export function DirectorSetupOfferPage({
  variant,
  identity,
  onSettled,
}: {
  variant: 'default' | 'from-sign';
  identity?: ReactNode;
  onSettled: (outcome: BoardDirectorSetupOutcome) => void;
}) {
  const { setupOffer } = useBoardDirectorHost();
  if (setupOffer === null) return null;
  return (
    <DirectorShellTakeover
      title={setupOffer.title}
      description={setupOffer.description}
      identity={identity}
      testId="page-director-setup-offer"
    >
      {setupOffer.render({ variant, onSettled })}
    </DirectorShellTakeover>
  );
}
